import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import "../../css/StaffHoliday/StaffHoliday.css";
import "../../css/Marketing/FinancialPromotions/FinancialPromotions.css";

import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import StaffHolidayTrackerPanel from "./StaffHolidayTrackerPanel";
import StaffHolidayExcelDownload from "./components/StaffHolidayExcelDownload";
import { fetchStaffHolidayDirectoryWithBalances } from "./utils/staffHolidayExcelImport";
import { useStaffHolidayRequests } from "./hooks/useStaffHolidayRequests";
import { useUserPermissions } from "../SignIn/UserPermissionsContext";
import { auth } from "../../backend/firebaseConfig";
import { Helmet } from "react-helmet-async";

const StaffHolidayTrackerPage = () => {
  const location = useLocation();

  const initialYear = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const yearFromQuery = Number(searchParams.get("year"));

    if (Number.isInteger(yearFromQuery) && yearFromQuery >= 2000) {
      return yearFromQuery;
    }

    return new Date().getFullYear();
  }, [location.search]);

  const [year, setYear] = useState(initialYear);
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [staffProfilesLoading, setStaffProfilesLoading] = useState(false);
  const currentUserEmail =
    auth.currentUser?.email || localStorage.getItem("userEmail") || "";
  const { userPermissions = [] } = useUserPermissions();
  const canDownloadExcel = useMemo(
    () =>
      userPermissions.some((permission) =>
        ["super holiday admin", "administrator"].includes(
          String(permission || "").trim().toLowerCase(),
        ),
      ),
    [userPermissions],
  );

  const { requests, loading: requestsLoading } = useStaffHolidayRequests({
    currentUserEmail,
    mode: canDownloadExcel ? "admin" : "mine",
  });

  useEffect(() => {
    setYear(initialYear);
  }, [initialYear]);

  useEffect(() => {
    let cancelled = false;

    const loadStaffProfiles = async () => {
      if (!canDownloadExcel) {
        setStaffProfiles([]);
        return;
      }

      setStaffProfilesLoading(true);

      try {
        const profiles = await fetchStaffHolidayDirectoryWithBalances({ year });

        if (!cancelled) {
          setStaffProfiles(profiles);
        }
      } catch (error) {
        if (!cancelled) {
          setStaffProfiles([]);
        }
      } finally {
        if (!cancelled) {
          setStaffProfilesLoading(false);
        }
      }
    };

    loadStaffProfiles();

    return () => {
      cancelled = true;
    };
  }, [canDownloadExcel, year]);

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Holiday Tracker</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <main className="staff-holiday-page">
          <Breadcrumb
            items={[
              { label: "All Staff", to: "/Department/AllStaff" },
              { label: "My Holiday", to: "/staff-holiday" },
              { label: "Holiday Tracker" },
            ]}
          />

          <div className="staff-holiday-header">
              <h3 className="FP_H3 staff-holiday-title">Holiday Tracker</h3>


            {canDownloadExcel && (
              <StaffHolidayExcelDownload
                staffProfiles={staffProfiles}
                requests={requests}
                summaryYear={year}
                disabled={staffProfilesLoading || requestsLoading}
              />
            )}
          </div>

          <StaffHolidayTrackerPanel
            initialYear={year}
            initialMonthIndex={new Date().getMonth()}
            departmentId="all"
            showDepartmentFilter
            onYearChange={setYear}
          />
        </main>
      </div>
    </div>
  );
};

export default StaffHolidayTrackerPage;
